import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, X } from "lucide-react";
import jsQR from "jsqr";
import { toast } from "sonner";

function Scanner({ onClose }) {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stream, raf, done = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      const v = videoRef.current;
      if (done || !v) return;
      if (v.readyState === v.HAVE_ENOUGH_DATA) {
        canvas.width = v.videoWidth; canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
        const code = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
        if (code?.data) {
          const m = code.data.match(/\/e\/([^/?#]+)\/a\/([^/?#]+)/i);
          if (m) { done = true; onClose(); navigate(`/${localStorage.getItem("bg_lang") || "en"}/e/${m[1]}/a/${m[2]}`); return; }
          toast.error("Not an artwork QR code");
        }
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => { stream = s; videoRef.current.srcObject = s; videoRef.current.play(); raf = requestAnimationFrame(tick); })
      .catch(() => setErr("Camera access was denied."));
    return () => { done = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [navigate, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="qr-scanner">
      <div className="flex justify-between items-center p-4 text-white">
        <span className="text-xs tracking-[0.3em] uppercase">Scan artwork QR</span>
        <button type="button" onClick={onClose} aria-label="Close scanner" className="ctrl" data-testid="qr-scanner-close"><X size={22} /></button>
      </div>
      {err ? <p className="text-center text-[var(--muted)] mt-20" data-testid="qr-scanner-error">{err}</p> : <video ref={videoRef} className="flex-1 object-cover w-full" muted playsInline />}
    </div>
  );
}

export default function QRScanButton({ label = "Scan QR code" }) {
  const [open, setOpen] = useState(false);
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={label} data-testid="qr-scan-button" className="btn-accent flex-none w-[52px] min-w-[44px] min-h-[44px] flex items-center justify-center">
        <QrCode size={24} />
      </button>
      {open && <Scanner onClose={() => setOpen(false)} />}
    </>
  );
}
